import React, {useState, useContext, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { convertSingleValueOnly, convertSingleUnit, currencyToSymbol } from '../../utils/commonUtils';
import { ChevronsDown } from 'lucide-react';
import SelectColumnContainer from './SelectColumnContainer';
import SaveSettingButton from './SaveSettingButton';
import { useDashboardContext } from '../../../context/DashboardContext';

import { UserSettingsContext, getDescriptionBySettingName, getDescriptionName } from "../../../context/UserSettingsContext";
import Loading from '../../Loading';
import { fetchIndustryMarketData } from "../../api/api_calls";
import ExchangeIcon from '../../utils/ExchangeIcon';


// 最新年度のデータを取得して、columnでソートする関数
const getSortedDataByLatestYear = (data, column) => {
  // 最新年度を取得
  // const latestFiscalYear = Math.max(...industryFinancialData.map(data => data.fiscal_year));

  // 最新年度のみのデータをフィルタリングし、columnで降順にソート
  const sortedData = data
    .sort((a, b) => b[column] - a[column]);  
  return sortedData;
};

const IndustryRanking = ({ industryFinancialData, secCode, industryCompanies }) => {
    const [open, setOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext();
    const marketColumns = ['stock_price', 'market_value', 'per', 'pbr', 'psr', 'volume'];
    const [industryMarketData, setIndustryMarketData] = useState(null);
    const {selectableAccounts} = useContext(UserSettingsContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => {
        if (selectableAccounts) {
            setSelectedAccount(selectableAccounts.find((account) => account.col==='sales'))
        }
    }, [selectableAccounts])

    useEffect(() => {
        if (marketColumns.includes(selectedAccount?.col) && !industryMarketData) {
            const getIndustryMarketData = async () => {
                let industryData;
                if (dashboardState.industryMarketData) {
                    industryData = dashboardState.industryMarketData;
                } else {
                    const fetchedData = await fetchIndustryMarketData(secCode, null, navigate, location);
                    industryData = fetchedData.data;
                    dashboardDispatch({ type: 'SET_INDUSTRY_MARKET_DATA', payload: industryData})
                }
                setIndustryMarketData(industryData);
            }
            getIndustryMarketData();
        }
    }, [selectedAccount]);

    const handleClick = () => {
        setOpen(!open);
    }

    const handleCompanyClick = (secCode) => {
        dashboardDispatch({type: 'SET_SEC_CODE', payload: secCode})
    }

    const getIndustryCompanyInfo = (secCode) => {
        return industryCompanies.find(company => company.sec_code === secCode);
    }

    
  if (!industryFinancialData) {
    return <Loading />
  }

    // 最新年度のデータでランキングを作成
    return (
        <div className='dashboard-row'>
        <h3>業界内順位</h3><SelectColumnContainer selectedAccount={selectedAccount} setSelectedAccount={setSelectedAccount} />
        <div className="dashboard-container align-center">
            {selectedAccount ?
                <div className={`disclosure-table industry-ranking ${open ? "expanded" : ""}`}>
                <table className="company-table">
                    <thead>
                    <tr>
                        <th className='text-right'>順位</th>
                        <th className='text-left'>証券コード</th>
                        <th className='text-left !pl-[15px]'>企業名</th>
                        <th className='text-right'>{selectedAccount.col_name}</th>
                        {/* <th>{secondColumnName}</th> */}
                    </tr>
                    </thead>
                    <tbody>
                        {marketColumns.includes(selectedAccount.col) ?
                            (industryMarketData) && 
                                getSortedDataByLatestYear(industryMarketData, selectedAccount.col).map((company, index) => (
                                    <tr key={company.sec_code} className={`${company.sec_code===secCode ? "bg-[#f3fff3]" : ""}`}>
                                    <td className='text-right'>{index + 1}</td>
                                    <td className='text-left'>{company.sec_code}</td>
                                    <td className='text-left'><span className="company-name-hover-bg cursor-pointer hover:text-white px-2 py-1 rounded-full" onClick={() => {handleCompanyClick(company.sec_code)}}>{company.name || "会社名"}</span></td>
                                    <td className='text-right'>{convertSingleValueOnly(company[selectedAccount.col])}{convertSingleUnit(company[selectedAccount.col])}</td>
                                    {/* <td>{convertSingleValueOnly(company[secondColumn])}{convertSingleUnit(company[secondColumn])}</td> */}
                                    </tr>
                                ))
                        :
                        getSortedDataByLatestYear(industryFinancialData, selectedAccount.col).map((company, index) => (
                            <tr key={company.sec_code} className={company.sec_code===secCode ? "bg-[#f3fff3]" : ""}>
                            <td className='text-right'>{index + 1}</td>
                            <td className='text-left'>{company.sec_code}</td>
                            <td className='flex text-left'>
                                <div>
                                    <span className="company-name-hover-bg cursor-pointer hover:text-white px-2 py-1 rounded-full" onClick={() => {handleCompanyClick(company.sec_code)}}>{company.company_name || "会社名"}</span>
                                </div>
                                <div>
                                    <div className='flex'>
                                        <div className='mr-2'>
                                            ({getIndustryCompanyInfo(company.sec_code)?.exchangeshortname} 
                                        </div>
                                        <ExchangeIcon exchange={getIndustryCompanyInfo(company.sec_code)?.exchangeshortname} />)
                                    </div>
                                </div>
                                
                            </td>
                            <td className='text-right'>{convertSingleValueOnly(company[selectedAccount.col])}{convertSingleUnit(company[selectedAccount.col])}{currencyToSymbol(company.reported_currency)}</td>
                            {/* <td>{convertSingleValueOnly(company[secondColumn])}{convertSingleUnit(company[secondColumn])}</td> */}
                            </tr>
                        ))
                    }
                    </tbody>
                </table>
              </div>
            :
            <Loading />
            }
            
            <div className='flex justify-center items-center w-full'>
                <ChevronsDown
                    className={`
                        ${open ? "rotate-180" : ""}
                        text-gray-500 hover:text-gray-600
                        hover:bg-gray-200
                        rounded-full
                        p-1 transition duration-200
                    `}
                    onClick={handleClick}
                    />
            </div>
        </div>
        </div>
    );
};

export default IndustryRanking;
